/**
 * Track export, porting `control/Export.js` and `format/{Gpx,Kml,Csv}.js`.
 *
 * Voice hints are deliberately absent: the legacy client only emitted them when
 * `turnInstructionMode > 1`, and this deployment sets `profilesUrl = null`, so the profile
 * text never loaded and that value never exceeded its default. The generated GPX therefore
 * matches what the fork actually produced.
 */

import {
  BRouterFeatureProperties,
  BRouterSegment,
  ExportFormat,
  LatLng
} from '../models/brouter-map.model';

/** Properties that are summed rather than taken from the first leg. */
const SUMMED_PROPERTIES = [
  'cost',
  'filtered ascend',
  'plain-ascend',
  'total-energy',
  'total-time',
  'track-length'
] as const;

export interface TotalTrack {
  coordinates: number[][];
  properties: BRouterFeatureProperties;
}

/**
 * Port of `BR.Export._concatTotalTrack`: merges the routed legs into one track.
 *
 * Adjacent legs share a coordinate, so the duplicate is dropped — keeping whichever of
 * the pair carries elevation, since a beeline endpoint has none.
 */
export const concatTotalTrack = (segments: readonly (BRouterSegment | null)[]): TotalTrack => {
  let coordinates: number[][] = [];
  let properties: BRouterFeatureProperties | undefined;

  segments.forEach((segment, segmentIndex) => {
    if (!segment) {
      return;
    }

    const featureProperties = segment.properties;

    if (properties) {
      for (const key of SUMMED_PROPERTIES) {
        properties[key] = (
          Number(properties[key] ?? 0) + Number(featureProperties[key] ?? 0)
        ).toString();
      }
      if (properties.messages && featureProperties.messages) {
        properties.messages = properties.messages.concat(featureProperties.messages.slice(1));
      }
    } else {
      properties = { ...featureProperties };
      if (properties.messages) {
        properties.messages = properties.messages.slice();
      }
    }

    let featureCoordinates = segment.geometry.coordinates;
    if (segmentIndex > 0 && coordinates.length > 0) {
      const previousLast = coordinates[coordinates.length - 1];
      const first = featureCoordinates[0];
      // Keep the richer of the two: a beeline endpoint has no elevation component.
      if (previousLast.length < first.length) {
        coordinates.pop();
      } else {
        featureCoordinates = featureCoordinates.slice(1);
      }
    }
    coordinates = coordinates.concat(featureCoordinates);
  });

  return { coordinates, properties: properties ?? {} };
};

const escapeXml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

/** `14833` becomes `4h 7m 13s`, matching BRouter's `OsmTrack.getFormattedTime2`. */
export const formatGpxTime = (seconds: number): string => {
  const hours = Math.trunc(seconds / 3600);
  const minutes = Math.trunc((seconds - hours * 3600) / 60);
  const remaining = seconds - hours * 3600 - minutes * 60;

  let time = '';
  if (hours !== 0) {
    time += `${hours}h `;
  }
  if (minutes !== 0) {
    time += `${minutes}m `;
  }
  if (remaining !== 0) {
    time += `${remaining}s`;
  }
  return time;
};

/** Port of `BR.Gpx._statsComment`. */
export const buildStatsComment = (properties: BRouterFeatureProperties): string => {
  if (Object.keys(properties).length === 0) {
    return '';
  }

  let comment = '<!--';
  comment += ` track-length = ${properties['track-length']}`;
  comment += ` filtered ascend = ${properties['filtered ascend']}`;
  comment += ` plain-ascend = ${properties['plain-ascend']}`;
  comment += ` cost=${properties['cost']}`;
  if (properties['total-energy']) {
    comment += ` energy=${(Number(properties['total-energy']) / 3600000).toFixed(1)}kwh`;
  }
  if (properties['total-time']) {
    comment += ` time=${formatGpxTime(Number(properties['total-time']))}`;
  }
  comment += ' -->';
  return comment;
};

export interface ExportOptions {
  trackname?: string;
  /** Route waypoints emitted alongside the track, for formats that support them. */
  waypoints?: readonly LatLng[];
  creator?: string;
}

export const formatGpx = (track: TotalTrack, options: ExportOptions = {}): string => {
  const name = options.trackname || 'brouter';
  const creator = options.creator ?? 'Smart Quick Sim';

  const waypoints = (options.waypoints ?? [])
    .map(
      point => `  <wpt lon="${point.lng}" lat="${point.lat}"><name>${escapeXml(name)}</name></wpt>`
    )
    .join('\n');

  const trackPoints = track.coordinates
    .map(([lng, lat, elevation]) => {
      const elevationTag = elevation !== undefined ? `<ele>${elevation}</ele>` : '';
      return `      <trkpt lon="${lng}" lat="${lat}">${elevationTag}</trkpt>`;
    })
    .join('\n');

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    buildStatsComment(track.properties),
    `<gpx version="1.1" creator="${escapeXml(creator)}" xmlns="http://www.topografix.com/GPX/1/1">`,
    waypoints,
    '  <trk>',
    `    <name>${escapeXml(name)}</name>`,
    '    <trkseg>',
    trackPoints,
    '    </trkseg>',
    '  </trk>',
    '</gpx>'
  ]
    .filter(line => line !== '')
    .join('\n');
};

export const formatKml = (track: TotalTrack, options: ExportOptions = {}): string => {
  const name = options.trackname || 'brouter';
  const coordinates = track.coordinates
    .map(([lng, lat, elevation]) => `${lng},${lat},${elevation ?? 0}`)
    .join(' ');

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<kml xmlns="http://www.opengis.net/kml/2.2">',
    '  <Document>',
    `    <name>${escapeXml(name)}</name>`,
    '    <Placemark>',
    `      <name>${escapeXml(name)}</name>`,
    '      <LineString>',
    `        <coordinates>${coordinates}</coordinates>`,
    '      </LineString>',
    '    </Placemark>',
    '  </Document>',
    '</kml>'
  ].join('\n');
};

export const formatGeoJson = (track: TotalTrack, options: ExportOptions = {}): string => {
  const properties = { ...track.properties };
  if (options.trackname) {
    properties.name = options.trackname;
  }

  return JSON.stringify(
    {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties,
          geometry: { type: 'LineString', coordinates: track.coordinates }
        }
      ]
    },
    null,
    2
  );
};

/** Port of `BR.Csv.format`: the raw message table, tab separated, header row included. */
export const formatCsv = (track: TotalTrack): string => {
  const messages = track.properties.messages ?? [];
  return messages.map(entries => entries.join('\t')).join('\n') + (messages.length ? '\n' : '');
};

export const MIME_TYPES: Record<ExportFormat, string> = {
  gpx: 'application/gpx+xml;charset=utf-8',
  kml: 'application/vnd.google-earth.kml+xml;charset=utf-8',
  geojson: 'application/vnd.geo+json;charset=utf-8',
  csv: 'text/tab-separated-values;charset=utf-8'
};

export const formatTrack = (
  format: ExportFormat,
  track: TotalTrack,
  options: ExportOptions = {}
): string => {
  switch (format) {
    case 'gpx':
      return formatGpx(track, options);
    case 'kml':
      return formatKml(track, options);
    case 'geojson':
      return formatGeoJson(track, options);
    case 'csv':
      return formatCsv(track);
  }
};

/**
 * Port of the legacy trackname validation: the server strips anything outside this set,
 * see `ServerHandler.getTrackName`.
 */
export const sanitiseTrackname = (name: string, allowedChars: string): string =>
  name.replace(new RegExp(`[^${allowedChars}]`, 'g'), '');
